import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Badge } from "@/shared/components/ui/badge";
import { Checkbox } from "@/shared/components/ui/checkbox";
import type { Order } from "../../types/order.types";
import {
  paymentStatusLabels,
  getStatusConfig,
} from "../../constants/orders.constants";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";

interface OrdersTableProps {
  orders: Order[];
  totalPages: number;
  currentPage: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedOrderIds: Set<string>;
  onToggleOrder: (id: string) => void;
  onToggleAll: () => void;
  isAllSelected: boolean;
  isSomeSelected: boolean;
}

export function OrdersTable({
  orders,
  totalPages,
  currentPage,
  page,
  pageSize,
  onPageChange,
  selectedOrderIds,
  onToggleOrder,
  onToggleAll,
  isAllSelected,
  isSomeSelected,
}: OrdersTableProps) {
  const navigate = useNavigate();

  const formatPassengers = (passengers: Order["passengers"]) => {
    const counts = passengers.reduce((acc, passenger) => {
      acc[passenger.type] = (acc[passenger.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const parts: string[] = [];

    if (counts.adult) {
      parts.push(`${counts.adult} взр`);
    }
    if (counts.child) {
      parts.push(`${counts.child} реб`);
    }
    if (counts.infant) {
      parts.push(`${counts.infant} млд`);
    }
    return parts.join(", ");
  };

  return (
    <div className="bg-white rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
                onCheckedChange={onToggleAll}
                aria-label="Выбрать все"
              />
            </TableHead>
            <TableHead>№ заказа</TableHead>
            <TableHead>Дата</TableHead>
            <TableHead>Маршрут</TableHead>
            <TableHead>Авиакомпания</TableHead>
            <TableHead>Оператор</TableHead>
            <TableHead>Пассажиры</TableHead>
            <TableHead>Наценка</TableHead>
            <TableHead>Цена поставщика</TableHead>
            <TableHead>Сумма</TableHead>
            <TableHead>Оплата</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <TableRow
              key={order.id}
              data-selected={selectedOrderIds.has(String(order.id))}
              className="data-[selected=true]:bg-blue-50"
            >
              <TableCell className="w-10">
                <Checkbox
                  checked={selectedOrderIds.has(String(order.id))}
                  onCheckedChange={() => onToggleOrder(String(order.id))}
                  aria-label={`Выбрать заказ ${order.id}`}
                />
              </TableCell>
              <TableCell className="font-medium">{order.id}</TableCell>
              <TableCell>
                {format(new Date(order.departureDate), "dd.MM.yyyy", {
                  locale: ru,
                })}
                {(order.returnFlight?.departureDate ?? order.returnDate) &&
                  ` - ${format(
                    new Date(order.returnFlight?.departureDate ?? order.returnDate!),
                    "dd.MM.yyyy",
                    { locale: ru }
                  )}`}
              </TableCell>
              <TableCell>
                {order.from.city}
                {order.middleCity?.trim() && ` → ${order.middleCity.trim()}`}
                {" → "}
                {order.to.city}
                {order.returnFlight && ` → ${order.returnFlight.to.city}`}
              </TableCell>
              <TableCell>{order.airline}</TableCell>
              <TableCell>{order.operator}</TableCell>
              <TableCell>{order.passengersSummary}</TableCell>
              <TableCell>{order?.markup?.toLocaleString("ru-RU")} ₸</TableCell>
              <TableCell>
                {order?.originalPrice?.toLocaleString("ru-RU")} ₸
              </TableCell>
              <TableCell>
                {order.totalPrice.toLocaleString("ru-RU")} ₸
              </TableCell>
              <TableCell>{paymentStatusLabels[order.paymentStatus]}</TableCell>
              <TableCell>
                <Badge className={getStatusConfig(order.status).className}>
                  {getStatusConfig(order.status).label}
                </Badge>
              </TableCell>
              <TableCell>
                <button
                  onClick={() => navigate(`/orders/${order.id}`)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex justify-end items-center gap-2 mt-4">
        <span className="text-sm text-muted-foreground">
          {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
